/* ==========================================================================
   Adımora JavaScript — Vanilla, no dependencies
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- FAQ Accordion ---------- */
  class AdimoraFaq extends HTMLElement {
    connectedCallback() {
      this.triggers = this.querySelectorAll('[data-adimora-faq-trigger]');
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.toggle.bind(this, trigger));
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle(trigger);
          }
        });
      });
    }

    toggle(trigger) {
      const contentId = trigger.getAttribute('aria-controls');
      const content = document.getElementById(contentId);
      if (!content) return;

      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        trigger.setAttribute('aria-expanded', 'false');
        content.setAttribute('data-open', 'false');
        content.style.maxHeight = '0';
      } else {
        // Close others in the same group
        this.triggers.forEach((t) => {
          if (t !== trigger) {
            t.setAttribute('aria-expanded', 'false');
            const otherContent = document.getElementById(t.getAttribute('aria-controls'));
            if (otherContent) {
              otherContent.setAttribute('data-open', 'false');
              otherContent.style.maxHeight = '0';
            }
          }
        });
        trigger.setAttribute('aria-expanded', 'true');
        content.setAttribute('data-open', 'true');
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    }
  }

  if (!customElements.get('adimora-faq')) {
    customElements.define('adimora-faq', AdimoraFaq);
  }

  /* ---------- Sticky Add-to-Cart Bar ---------- */
  class AdimoraStickyAtc extends HTMLElement {
    connectedCallback() {
      this.bar = this;
      this.triggerSelector = this.dataset.triggerSection || '[data-adimora-offer]';
      this.footerEl = document.querySelector('.section-footer, footer');
      this.isDesktopEnabled = this.dataset.showDesktop === 'true';
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.comparePriceEl = this.querySelector('[data-sticky-compare-price]');
      this.nameEl = this.querySelector('[data-sticky-variant-name]');
      this.stickyForm = this.querySelector('form');
      this.stickyVariantInput = this.querySelector('[data-sticky-variant-id]');

      // Add body class
      document.body.classList.add('adimora-has-sticky-bar');

      // Observe when the offer section passes out of view
      this.setupIntersectionObserver();

      // Listen for variant changes from Dawn's pubsub
      this.listenForVariantChanges();
    }

    setupIntersectionObserver() {
      const form = document.querySelector('#adimora-product-form')
        || document.querySelector('.section-adimora-main-product')
        || document.querySelector('[data-adimora-offer]');

      if (!form) return;

      const updateSticky = () => {
        if (document.querySelector('cart-drawer.active, cart-drawer[open], .drawer.active')) {
          this.hide();
          return;
        }

        const rect = form.getBoundingClientRect();
        // Sticky bar becomes visible ONLY when the product form is completely scrolled off top of viewport
        if (rect.bottom < 0) {
          this.show();
        } else {
          this.hide();
        }
      };

      window.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky, { passive: true });
      document.addEventListener('cart-drawer:open', () => this.hide());
      updateSticky();
    }

    show() {
      this.bar.classList.add('is-visible');
      this.bar.setAttribute('aria-hidden', 'false');
      document.body.classList.add('adimora-sticky-visible');
    }

    hide() {
      this.bar.classList.remove('is-visible');
      this.bar.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('adimora-sticky-visible');
    }

    listenForVariantChanges() {
      // Listen to Dawn's variant change events via pubsub
      if (typeof subscribe === 'function') {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.updateFromVariant(event.data);
        });
      }

      // Also listen to custom event from our package selector
      document.addEventListener('adimora:variant-change', (e) => {
        this.updateFromVariant(e.detail);
      });
    }

    updateFromVariant(data) {
      if (!data || !data.variant) return;
      const variant = data.variant;

      // Update sticky variant ID
      if (this.stickyVariantInput) {
        this.stickyVariantInput.value = variant.id;
      }

      // Update price
      if (this.priceEl && variant.price != null) {
        this.priceEl.textContent = this.formatMoney(variant.price);
      }

      // Update compare price
      if (this.comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          this.comparePriceEl.style.display = '';
        } else {
          this.comparePriceEl.style.display = 'none';
        }
      }

      // Update variant name
      if (this.nameEl && variant.title) {
        this.nameEl.textContent = variant.title;
      }

      // Update button state
      const btn = this.querySelector('[data-sticky-add-btn]');
      if (btn) {
        if (variant.available) {
          btn.disabled = false;
          btn.querySelector('.adimora-btn__text').textContent =
            btn.dataset.addText || 'Kapıda Ödeme ile Sipariş Ver';
        } else {
          btn.disabled = true;
          btn.querySelector('.adimora-btn__text').textContent =
            btn.dataset.soldOutText || 'Tükendi';
        }
      }
    }

    formatMoney(cents) {
      // Shopify stores prices in cents (subunits)
      const amount = (cents / 100).toFixed(2);
      return amount.replace('.', ',') + ' TL';
    }
  }

  if (!customElements.get('adimora-sticky-atc')) {
    customElements.define('adimora-sticky-atc', AdimoraStickyAtc);
  }

  /* ---------- Package Selector ---------- */
  class AdimoraPackageSelector extends HTMLElement {
    connectedCallback() {
      this.packages = this.querySelectorAll('[data-adimora-package]');
      this.variantInput = this.closest('form')?.querySelector('[name="id"]')
        || this.querySelector('[name="id"]');
      this.priceDisplay = this.querySelector('[data-package-price]');
      this.comparePriceDisplay = this.querySelector('[data-package-compare-price]');

      this.packages.forEach((pkg) => {
        pkg.addEventListener('click', () => this.select(pkg));
        const radio = pkg.querySelector('input[type="radio"]');
        if (radio) {
          radio.addEventListener('change', () => this.select(pkg));
        }
      });

      // Select initial
      const checked = this.querySelector('input[type="radio"]:checked');
      if (checked) {
        this.select(checked.closest('[data-adimora-package]'));
      }
    }

    select(pkg) {
      // Update visual state
      this.packages.forEach((p) => p.classList.remove('is-selected'));
      pkg.classList.add('is-selected');

      // Check radio
      const radio = pkg.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;

      const variantId = pkg.dataset.variantId;
      const price = parseInt(pkg.dataset.price, 10);
      const comparePrice = parseInt(pkg.dataset.comparePrice, 10);
      const variantTitle = pkg.dataset.variantTitle || '';
      const available = pkg.dataset.available !== 'false';

      // Update hidden variant input in the form
      if (this.variantInput) {
        this.variantInput.value = variantId;
      }

      // Also update any other forms on page that reference the same product
      document.querySelectorAll('[data-adimora-variant-id-sync]').forEach((input) => {
        input.value = variantId;
      });

      // Dispatch custom event for sticky bar and other listeners
      document.dispatchEvent(
        new CustomEvent('adimora:variant-change', {
          detail: {
            variant: {
              id: parseInt(variantId, 10),
              price: price,
              compare_at_price: comparePrice || null,
              title: variantTitle,
              available: available,
            },
          },
        })
      );

      // Update inline price display
      if (this.priceDisplay) {
        this.priceDisplay.textContent = this.formatMoney(price);
      }
      if (this.comparePriceDisplay) {
        if (comparePrice && comparePrice > price) {
          this.comparePriceDisplay.textContent = this.formatMoney(comparePrice);
          this.comparePriceDisplay.style.display = '';
        } else {
          this.comparePriceDisplay.style.display = 'none';
        }
      }

      // Update add to cart button
      const addBtn = this.closest('section')?.querySelector('[data-adimora-add-btn]');
      if (addBtn) {
        if (available) {
          addBtn.disabled = false;
          const textEl = addBtn.querySelector('.adimora-btn__text');
          if (textEl) textEl.textContent = addBtn.dataset.addText || 'Kapıda Ödeme ile Sipariş Ver';
        } else {
          addBtn.disabled = true;
          const textEl = addBtn.querySelector('.adimora-btn__text');
          if (textEl) textEl.textContent = addBtn.dataset.soldOutText || 'Tükendi';
        }
      }
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return amount.replace('.', ',') + ' TL';
    }
  }

  if (!customElements.get('adimora-package-selector')) {
    customElements.define('adimora-package-selector', AdimoraPackageSelector);
  }

  /* ---------- Fade-in Observer ---------- */
  if ('IntersectionObserver' in window) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!prefersReducedMotion) {
      const fadeEls = document.querySelectorAll('.adimora-fade-in');
      const fadeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              fadeObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );
      fadeEls.forEach((el) => fadeObserver.observe(el));
    }
  }

  /* ---------- Sticky form sync with product form ---------- */
  const updateStickyAjaxState = (form, state) => {
    const button = form.querySelector('button[type="submit"]');
    const label = button?.querySelector('.adimora-btn__text');
    if (!button || !label) return;

    const defaultText = button.dataset.addText || 'Kapıda Ödeme ile Sipariş Ver';
    if (state === 'loading') {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = 'Ekleniyor…';
      return;
    }

    button.disabled = false;
    button.setAttribute('aria-busy', 'false');
    if (state === 'error') {
      label.textContent = 'Tekrar dene';
      button.setAttribute('aria-label', 'Sepete eklenemedi. Tekrar dene');
      button.setAttribute('aria-live', 'polite');
      return;
    }

    label.textContent = defaultText;
    button.removeAttribute('aria-label');
    button.removeAttribute('aria-live');
  };

  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form.matches('[data-adimora-sticky-form]')) return;

    // Prevent double submission — use the main product form instead
    e.preventDefault();

    const variantId = form.querySelector('[name="id"]')?.value;
    if (!variantId) return;

    const mainForm = document.querySelector(
      'form[action="/cart/add"]:not([data-adimora-sticky-form])'
    );

    if (mainForm) {
      // Sync variant to main form and submit it
      const mainInput = mainForm.querySelector('[name="id"]');
      if (mainInput) mainInput.value = variantId;

      // Use Dawn's ProductForm submit if available
      const productForm = mainForm.closest('product-form');
      if (productForm && typeof productForm.onSubmitHandler === 'function') {
        const fakeEvent = new Event('submit', { bubbles: true, cancelable: true });
        Object.defineProperty(fakeEvent, 'target', { value: mainForm });
        productForm.onSubmitHandler(fakeEvent);
      } else {
        // Fallback: AJAX cart add
        updateStickyAjaxState(form, 'loading');
        const formData = new FormData(mainForm);
        // ui-craft-detect-ignore-next-line — explicit loading/error UI is exposed above.
        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        })
          .then((res) => {
            if (!res.ok) {
              updateStickyAjaxState(form, 'error');
              return null;
            }
            return res.json();
          })
          .then((data) => {
            if (!data) return null;
            // Open cart drawer if available
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              return fetch('/cart.js')
                .then((r) => {
                  if (!r.ok) {
                    updateStickyAjaxState(form, 'error');
                    return null;
                  }
                  return r.json();
                })
                .then((cart) => {
                  if (!cart) return null;
                  // Refresh cart drawer
                  cartDrawer.classList.add('active');
                  document.body.classList.add('overflow-hidden');
                  updateStickyAjaxState(form, 'idle');
                });
            } else {
              updateStickyAjaxState(form, 'idle');
            }
          })
          .catch(() => {
            updateStickyAjaxState(form, 'error');
          });
      }
    } else {
      // No main form found, submit directly via AJAX
      updateStickyAjaxState(form, 'loading');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      })
        .then((res) => {
          if (!res.ok) {
            updateStickyAjaxState(form, 'error');
            return null;
          }
          return res.json();
        })
        .then((data) => {
          if (!data) return;
          window.location.href = '/cart';
        })
        .catch(() => {
          updateStickyAjaxState(form, 'error');
        });
    }
  });
  /* ---------- Product Gallery & Components Initialization ---------- */
  function initAdimoraComponents(container = document) {
    // Accordion triggers
    container.querySelectorAll('[data-adimora-acc-trigger]').forEach((trigger) => {
      if (trigger.dataset.adimoraInitialized) return;
      trigger.dataset.adimoraInitialized = 'true';
      trigger.addEventListener('click', function () {
        const contentId = this.getAttribute('aria-controls');
        const content = document.getElementById(contentId);
        if (!content) return;
        const isOpen = this.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          this.setAttribute('aria-expanded', 'false');
          content.style.maxHeight = '0';
        } else {
          this.setAttribute('aria-expanded', 'true');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      });
    });

    // Gallery tracks
    container.querySelectorAll('[data-adimora-gallery-track]').forEach((track) => {
      if (track.dataset.adimoraInitialized) return;
      track.dataset.adimoraInitialized = 'true';
      const gallery = track.closest('[data-adimora-gallery]');
      if (!gallery) return;
      const dots = gallery.querySelectorAll('.adimora-gallery__dot');
      const slides = track.querySelectorAll('.adimora-gallery__slide');

      track.addEventListener('scroll', function () {
        const slideWidth = track.firstElementChild?.clientWidth || track.clientWidth;
        if (!slideWidth) return;
        const activeIndex = Math.round(track.scrollLeft / slideWidth);
        dots.forEach((dot, idx) => {
          if (idx === activeIndex) {
            dot.classList.add('is-active');
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.classList.remove('is-active');
            dot.setAttribute('aria-current', 'false');
          }
        });
      }, { passive: true });

      dots.forEach((dot, index) => {
        dot.addEventListener('click', function (e) {
          e.preventDefault();
          const slideIndex = parseInt(this.dataset.slide ?? index, 10);
          const targetSlide = slides[slideIndex];
          if (targetSlide) {
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const scrollBehavior = reduceMotion ? 'auto' : 'smooth';
            track.style.scrollBehavior = scrollBehavior;
            track.style.scrollSnapType = 'none';
            track.scrollTo({
              left: targetSlide.offsetLeft,
              behavior: scrollBehavior
            });
            dots.forEach((d, i) => {
              d.classList.toggle('is-active', i === slideIndex);
              d.setAttribute('aria-current', i === slideIndex ? 'true' : 'false');
            });
            setTimeout(() => {
              track.style.scrollSnapType = 'x mandatory';
            }, reduceMotion ? 0 : 350);
          }
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAdimoraComponents());
  } else {
    initAdimoraComponents();
  }
  document.addEventListener('shopify:section:load', (e) => initAdimoraComponents(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    if (!block) return;
    if (block.classList.contains('adimora-accordion')) {
      const trigger = block.querySelector('[data-adimora-acc-trigger]');
      if (trigger && trigger.getAttribute('aria-expanded') !== 'true') {
        trigger.click();
      }
    }
    const slide = block.closest('[data-slide-index]');
    if (slide) {
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  });

  /* ---------- Media Slider & Reviews Slider Custom Elements ---------- */
  if (!customElements.get('adimora-media-slider')) {
    customElements.define('adimora-media-slider', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized) return;
        this.dataset.initialized = 'true';
        this.initSlider();
        this.initVideos();
      }

      initSlider() {
        const track = this.querySelector('[data-slider-track]');
        const dots = this.querySelectorAll('[data-slider-dots] button');
        if (!track || !dots.length) return;

        track.addEventListener('scroll', () => {
          const slideWidth = track.firstElementChild?.clientWidth || track.clientWidth;
          if (!slideWidth) return;
          const activeIndex = Math.round(track.scrollLeft / slideWidth);
          dots.forEach((dot, idx) => {
            dot.classList.toggle('is-active', idx === activeIndex);
          });
        }, { passive: true });

        dots.forEach((dot, idx) => {
          dot.addEventListener('click', () => {
            const slide = track.children[idx];
            if (slide) {
              track.style.scrollBehavior = 'smooth';
              track.style.scrollSnapType = 'none';
              track.scrollTo({
                left: slide.offsetLeft,
                behavior: 'smooth'
              });
              setTimeout(() => {
                track.style.scrollSnapType = 'x mandatory';
              }, 350);
            }
          });
        });
      }

      initVideos() {
        const videos = this.querySelectorAll('video');
        if (!videos.length) return;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) return;

        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {});
              }
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.5 });

        videos.forEach((v) => observer.observe(v));
      }
    });
  }

  if (!customElements.get('adimora-reviews-slider')) {
    customElements.define('adimora-reviews-slider', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized) return;
        this.dataset.initialized = 'pending';
        const initializeWhenReady = () => {
          if (!this.querySelector('[data-slider-track]') || !this.querySelector('[data-ugc-modal]')) {
            requestAnimationFrame(initializeWhenReady);
            return;
          }
          if (this.dataset.initialized !== 'pending') return;
          this.dataset.initialized = 'true';
          this.initSlider();
          this.initModal();
        };
        requestAnimationFrame(initializeWhenReady);
      }

      initSlider() {
        const track = this.querySelector('[data-slider-track]');
        const dots = this.querySelectorAll('[data-slider-dots] button');
        if (!track || !dots.length) return;

        track.addEventListener('scroll', () => {
          const slideWidth = track.firstElementChild?.clientWidth || track.clientWidth;
          if (!slideWidth) return;
          const activeIndex = Math.round(track.scrollLeft / slideWidth);
          dots.forEach((dot, idx) => {
            dot.classList.toggle('is-active', idx === activeIndex);
          });
        }, { passive: true });

        dots.forEach((dot, idx) => {
          dot.addEventListener('click', () => {
            const slide = track.children[idx];
            if (slide) {
              track.style.scrollBehavior = 'smooth';
              track.style.scrollSnapType = 'none';
              track.scrollTo({
                left: slide.offsetLeft,
                behavior: 'smooth'
              });
              setTimeout(() => {
                track.style.scrollSnapType = 'x mandatory';
              }, 350);
            }
          });
        });
      }

      initModal() {
        const modal = this.querySelector('[data-ugc-modal]');
        if (!modal) return;

        const modalImg = modal.querySelector('[data-ugc-modal-img]');
        const modalClose = modal.querySelector('[data-ugc-modal-close]');
        const modalPrev = modal.querySelector('[data-ugc-modal-prev]');
        const modalNext = modal.querySelector('[data-ugc-modal-next]');
        const modalRating = modal.querySelector('[data-ugc-modal-rating]');
        const modalText = modal.querySelector('[data-ugc-modal-text]');
        const modalAuthor = modal.querySelector('[data-ugc-modal-author]');
        const modalThumbs = modal.querySelector('[data-ugc-modal-thumbs]');

        let currentPhotos = [];
        let currentPhotoIndex = 0;
        let lastFocusedCard = null;

        const updateModalPhoto = (index) => {
          currentPhotoIndex = index;
          if (modalImg && currentPhotos[index]) {
            modalImg.src = currentPhotos[index];
          }
          if (modalThumbs) {
            modalThumbs.querySelectorAll('button').forEach((b, idx) => {
              b.classList.toggle('is-active', idx === index);
            });
          }
          if (modalPrev && modalNext) {
            modalPrev.style.display = currentPhotos.length > 1 ? 'flex' : 'none';
            modalNext.style.display = currentPhotos.length > 1 ? 'flex' : 'none';
          }
        };

        const closeModal = () => {
          modal.classList.remove('is-open');
          modal.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
          lastFocusedCard?.focus();
        };

        const openModalForCard = (card) => {
          const p1 = card.getAttribute('data-photo-1');
          const p2 = card.getAttribute('data-photo-2');
          const p3 = card.getAttribute('data-photo-3');
          currentPhotos = [p1, p2, p3].filter(Boolean);
          if (!currentPhotos.length) return;

          if (modalThumbs) {
            modalThumbs.innerHTML = '';
            if (currentPhotos.length > 1) {
              currentPhotos.forEach((src, idx) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'adimora-ugc-modal__thumb-btn' + (idx === 0 ? ' is-active' : '');
                btn.innerHTML = `<img src="${src}" alt="Görsel ${idx + 1}" width="64" height="64" loading="lazy" decoding="async">`;
                btn.addEventListener('click', (e) => {
                  e.stopPropagation();
                  updateModalPhoto(idx);
                });
                modalThumbs.appendChild(btn);
              });
              modalThumbs.style.display = 'flex';
            } else {
              modalThumbs.style.display = 'none';
            }
          }

          const rating = card.dataset.rating;
          if (modalRating) {
            if (rating && rating !== 'none') {
              const stars = parseInt(rating, 10);
              let starsSvg = '';
              for (let i = 0; i < stars; i++) {
                starsSvg += '<svg viewBox="0 0 20 20" style="width:16px;height:16px;fill:#E4C5A5"><path d="M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z"/></svg>';
              }
              modalRating.innerHTML = starsSvg;
              modalRating.style.display = 'flex';
            } else {
              modalRating.style.display = 'none';
            }
          }

          if (modalText) {
            const text = card.dataset.text || '';
            modalText.innerHTML = text;
            modalText.style.display = text ? 'block' : 'none';
          }

          if (modalAuthor) {
            const author = card.dataset.author || '';
            const date = card.dataset.date || '';
            const verified = card.dataset.verified === 'true';
            const verifiedLabel = card.dataset.verifiedLabel || 'Doğrulanmış';
            
            let metaHtml = '';
            if (author) metaHtml += `<strong>${author}</strong>`;
            if (date) metaHtml += (metaHtml ? ' • ' : '') + `<span>${date}</span>`;
            if (verified) metaHtml += `<span style="color:#5F7F77;margin-left:6px;font-weight:600;">✓ ${verifiedLabel}</span>`;
            
            modalAuthor.innerHTML = metaHtml;
            modalAuthor.style.display = metaHtml ? 'block' : 'none';
          }

          updateModalPhoto(0);
          lastFocusedCard = card;
          modal.classList.add('is-open');
          modal.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
          modalClose?.focus();
        };

        this.addEventListener('click', (event) => {
          const card = event.target.closest?.('[data-ugc-card]');
          if (card && this.contains(card)) openModalForCard(card);
        });

        this.querySelectorAll('[data-ugc-card]').forEach((card) => {
          card.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            openModalForCard(card);
          });
        });

        if (modalClose) modalClose.addEventListener('click', closeModal);
        modal.addEventListener('click', (e) => {
          if (e.target === modal) closeModal();
        });

        if (modalPrev) {
          modalPrev.addEventListener('click', (e) => {
            e.stopPropagation();
            const prevIndex = (currentPhotoIndex - 1 + currentPhotos.length) % currentPhotos.length;
            updateModalPhoto(prevIndex);
          });
        }
        if (modalNext) {
          modalNext.addEventListener('click', (e) => {
            e.stopPropagation();
            const nextIndex = (currentPhotoIndex + 1) % currentPhotos.length;
            updateModalPhoto(nextIndex);
          });
        }

        document.addEventListener('keydown', (e) => {
          if (!modal.classList.contains('is-open')) return;
          if (e.key === 'Escape') closeModal();
          if (e.key === 'ArrowLeft' && currentPhotos.length > 1) {
            const prevIndex = (currentPhotoIndex - 1 + currentPhotos.length) % currentPhotos.length;
            updateModalPhoto(prevIndex);
          }
          if (e.key === 'ArrowRight' && currentPhotos.length > 1) {
            const nextIndex = (currentPhotoIndex + 1) % currentPhotos.length;
            updateModalPhoto(nextIndex);
          }
        });
      }
    });
  }
})();
